import { cn } from "@/components/podkit/lib/cn";
import { useEnvironment } from "@/queries/environment-queries";
import { VncScreen, type VncScreenHandle } from "react-vnc";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useDebouncedCallback } from "use-debounce";

/** Port exposed by the in-environment VNC proxy (noVNC websockify + HTTP resize). */
export const BROWSER_VNC_PROXY_PORT = 48222;

type ConnectionState = "idle" | "connecting" | "connected" | "disconnected" | "error";

/**
 * Sends the panel's pixel dimensions to the container's /resize endpoint
 * so Xvfb + Chromium match the available space exactly.
 */
function requestRemoteResize(baseUrl: string, width: number, height: number) {
    const w = Math.round(width * window.devicePixelRatio);
    const h = Math.round(height * window.devicePixelRatio);
    if (w < 320 || h < 240) return;

    fetch(`${baseUrl}/resize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ width: w, height: h }),
    }).catch(() => {
        // Intentionally swallowed – resize is best-effort; failures are harmless.
    });
}

export const BrowserVncPanel: FC<{
    environmentId: string;
    visible: boolean;
}> = ({ environmentId, visible }) => {
    const { data: environment } = useEnvironment(environmentId);
    const [connectionState, setConnectionState] = useState<ConnectionState>("idle");

    const vncRef = useRef<VncScreenHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Build the WebSocket URL and HTTP base URL from the environment port URL
    const { wsUrl, httpBaseUrl } = useMemo(() => {
        if (!environment) {
            return { wsUrl: undefined, httpBaseUrl: undefined };
        }
        const portStatus = environment.status?.environmentUrls?.ports?.find((p) => p.port === BROWSER_VNC_PROXY_PORT);
        if (!portStatus?.url) {
            return { wsUrl: undefined, httpBaseUrl: undefined };
        }
        const wsUrlObj = new URL(portStatus.url);
        wsUrlObj.protocol = wsUrlObj.protocol === "https:" ? "wss:" : "ws:";
        wsUrlObj.pathname = "/websockify";

        const httpUrlObj = new URL(portStatus.url);
        // Strip trailing slash for clean concatenation.
        const base = httpUrlObj.toString().replace(/\/$/, "");

        return { wsUrl: wsUrlObj.toString(), httpBaseUrl: base };
    }, [environment]);

    // Debounce resize requests so dragging a panel edge doesn't flood the endpoint.
    const debouncedResize = useDebouncedCallback(
        (url: string, w: number, h: number) => requestRemoteResize(url, w, h),
        250,
    );

    // Observe the container size and tell the remote display to resize.
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !httpBaseUrl) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            debouncedResize(httpBaseUrl, width, height);
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, [httpBaseUrl, debouncedResize]);

    // When the panel becomes visible, focus VNC and send a resize in case
    // the container dimensions changed while hidden.
    useEffect(() => {
        if (visible && vncRef.current && connectionState === "connected") {
            vncRef.current.focus();
            if (containerRef.current && httpBaseUrl) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                requestRemoteResize(httpBaseUrl, width, height);
            }
        }
    }, [visible, connectionState, httpBaseUrl]);

    const handleReconnect = useCallback(() => {
        if (vncRef.current) {
            vncRef.current.connect();
        }
        setConnectionState("connecting");
    }, []);

    if (!wsUrl) {
        return (
            <div className={cn("flex h-full items-center justify-center", !visible && "hidden")}>
                <div className="flex flex-col items-center gap-3 text-content-secondary">
                    <p className="text-sm">Browser not running.</p>
                    <p className="text-xs text-content-tertiary">
                        Start it with{" "}
                        <code className="rounded bg-surface-secondary px-1 py-0.5">
                            /usr/local/gitpod/shared/utils browser start
                        </code>
                    </p>
                </div>
            </div>
        );
    }

    const showLoading = connectionState === "idle" || connectionState === "connecting";
    const showError = connectionState === "error" || connectionState === "disconnected";

    return (
        <div ref={containerRef} className={cn("relative size-full overflow-hidden", !visible && "hidden")}>
            <VncScreen
                ref={vncRef}
                url={wsUrl}
                scaleViewport
                background="rgb(24, 24, 27)"
                rfbOptions={{ shared: true }}
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    visibility: showLoading || showError ? "hidden" : "visible",
                }}
                onConnect={() => setConnectionState("connected")}
                onDisconnect={(e) => {
                    if (e?.detail.clean) {
                        setConnectionState("disconnected");
                    } else {
                        setConnectionState("error");
                    }
                }}
            />

            {showLoading && !showError && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="size-5 animate-spin rounded-full border-2 border-content-tertiary border-t-content-primary" />
                        <p className="text-sm text-content-secondary">Connecting...</p>
                    </div>
                </div>
            )}

            {showError && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-content-secondary">
                        <p className="text-sm">{connectionState === "error" ? "Connection lost." : "Disconnected."}</p>
                        <button
                            onClick={handleReconnect}
                            data-tracking-id="reconnect"
                            className="hover:text-content-link-hover text-sm text-content-link underline"
                        >
                            Reconnect
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
